import { createContext, useContext, useEffect, useRef, useState } from 'react'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type Resolved = 'light' | 'dark'

type ThemeProviderState = {
  theme: Theme
  resolvedTheme: Resolved
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: 'system',
  resolvedTheme: 'light',
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'honey-show-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return defaultTheme
    try {
      const stored = localStorage.getItem(storageKey) as Theme | null
      return stored || defaultTheme
    } catch {
      return defaultTheme
    }
  })
  const mediaRef = useRef<MediaQueryList | null>(null)
  const [systemMode, setSystemMode] = useState<Resolved>(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  // On mount, clear any persisted non-system theme per unified policy
  useEffect(() => {
    const stored = localStorage.getItem(storageKey) as Theme | null
    if (stored && stored !== 'system') {
      localStorage.removeItem(storageKey)
      setTheme('system')
    }
  }, [storageKey])

  // Apply current theme to <html> class
  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove('light', 'dark')

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'

      root.classList.add(systemTheme)
      return
    }

    root.classList.add(theme)
  }, [theme])

  // Listen for system theme changes when using `system`
  useEffect(() => {
    if (theme !== 'system') return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    mediaRef.current = mql

    const apply = (isDark: boolean) => {
      const root = window.document.documentElement
      root.classList.remove('light', 'dark')
      root.classList.add(isDark ? 'dark' : 'light')
      setSystemMode(isDark ? 'dark' : 'light')
    }

    // Initial sync
    apply(mql.matches)

    const handler = (e: MediaQueryListEvent) => apply(e.matches)
    mql.addEventListener?.('change', handler)

    return () => {
      mql.removeEventListener?.('change', handler)
    }
  }, [theme])

  const resolvedTheme: Resolved = theme === 'system' ? systemMode : theme

  const value: ThemeProviderState = {
    theme,
    resolvedTheme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
